import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useCmsCollection } from "../hooks/useCms";
import { LightboxModal } from "./LightboxModal";
import { getOptimizedImageUrl } from "../lib/utils";

// Før & efter par med klare etiketter og autentiske billeder fra salonen
const BEFORE_AFTER_PAIRS = [
  {
    id: "lille-hund-klip",
    title: "Lille hund – klip & formning",
    description: "Fra pjusket, vildtvoksende pels til en let, velformet og ren frisure.",
    before: {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/787000834-122101068201462763-7340390738593124962-n.jpg",
      badge: "Før",
      detail: "Pjusket & uformet pels ved ankomst",
      alt: "Lille hund før klip med lang pjusket pels på trimmebordet",
    },
    after: {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/786938228-122101068339462763-8990561348318155175-n.jpg",
      badge: "Efter",
      detail: "Flot nyklippet, afrundet form & soignerede poter",
      alt: "Lille hund efter klip med flot form og ren pels på trimmebordet",
    },
  },
  {
    id: "collie-pelspleje",
    title: "Langhåret collie – filtret underuld & pelspleje",
    description: "Underuld skånsomt fjernet samt udredning og fjernelse af filt.",
    before: {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003451.jpg",
      badge: "Før",
      detail: "Tæt og filtret uld på bagpartiet",
      alt: "Langhåret hund med filtret pels på bagpartiet før behandling",
    },
    after: {
      url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003464.jpg",
      badge: "Efter",
      detail: "Gennemredt, let og glansfuld pelsfinish",
      alt: "Trefarvet collie i salonen efter pelspleje og udredning",
    },
  },
];

// Autentiske billeder fra projektets assets som garanteret fallback & kategorisering
const ASSET_GALLERY_IMAGES = [
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003537.jpg",
    title: "Cocker spaniel på trimmebord",
    alt: "Cocker spaniel stående trygt på klippebordet i hundesalonen",
    categories: ["klip", "vask-foen"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003534.jpg",
    title: "Hvalp svøbt i håndklæde efter vask",
    alt: "Sød lille hvalp svøbt i et blødt håndklæde efter vask i salonen",
    categories: ["hvalpe", "vask-foen"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003489.jpg",
    title: "Hundefrisør Trine med diplom & hund",
    alt: "Hundefrisør ved trimmebord med lille hund, diplom og blomsterbuket",
    categories: ["klip", "foer-efter"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003479.jpg",
    title: "Golden Retriever på trimmebord",
    alt: "Lys Golden Retriever på et blåt trimmebord i en hundesalon",
    categories: ["vask-foen", "klip"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003464.jpg",
    title: "Shetland Sheepdog i salonen",
    alt: "Trefarvet Shetland Sheepdog der sidder på gulvet i en hundesalon",
    categories: ["klip", "foer-efter"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003484.jpg",
    title: "Nyklippet lille hund",
    alt: "Nyklippet lille hvid hund stående på et blåt trimmebord i en hundesalon",
    categories: ["klip", "foer-efter"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003482.jpg",
    title: "Lille hund på hæve-sænkebord",
    alt: "Lille hvid hund på et blåt trimmebord i en hundesalon",
    categories: ["klip"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003461.jpg",
    title: "Pelspleje i salonen",
    alt: "Hundefrisør med mundbind og høreværn sammen med en trefarvet hund i salon",
    categories: ["vask-foen", "klip"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003452.jpg",
    title: "Langhåret collie på trimmebord",
    alt: "Langhåret trefarvet collie på et blåt trimmebord i hundesalon",
    categories: ["vask-foen", "klip"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003451.jpg",
    title: "Pelspleje før behandling",
    alt: "Langhåret hund med filtret pels på et blåt trimmebord",
    categories: ["foer-efter", "vask-foen"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/786938228-122101068339462763-8990561348318155175-n.jpg",
    title: "Klip og formning",
    alt: "Nyklippet lille hund på trimmebord i salonen",
    categories: ["klip", "foer-efter"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/787000834-122101068201462763-7340390738593124962-n.jpg",
    title: "Tryg pelspleje",
    alt: "Lille hund på trimmebord i et roligt miljø",
    categories: ["klip", "foer-efter"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/782385765-122097952311462763-9183792221188965560-n.jpg",
    title: "Vask og føn",
    alt: "Langhåret collie liggende afslappet på hæve-sænkebord",
    categories: ["klip", "vask-foen"],
  },
  {
    url: "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/781251279-122097952353462763-1032546948778040011-n.jpg",
    title: "Salonen og plejeprodukter",
    alt: "Langhåret collie i snor foran hylde med plejeprodukter",
    categories: ["vask-foen", "foer-efter"],
  },
];

export function GallerySection({
  categorySlug = null,
  title = "Glimt fra salonen",
  subtitle = "Se vores tilfredse firbenede kunder og hyggelige stunder fra salonen i Fredericia.",
  showTabs = true,
  maxImages = null,
  id = "galleri",
}) {
  const { items: gallerier, loading } = useCmsCollection("galleri");
  const { items: cmsBeforeAfterPairs } = useCmsCollection("foer_og_efter_galleri", {
    sort: "sort_order",
    order: "asc",
  });
  const [activeTab, setActiveTab] = useState(categorySlug || "alle");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Byg listen af billeder med DEDUPLIKERING så hvert asset-billede kun optræder én gang
  const displayedImages = useMemo(() => {
    const targetCategory = categorySlug || activeTab;

    // Hvis CMS har data
    if (gallerier && gallerier.length > 0) {
      const filteredGalleries = gallerier.filter((item) => {
        if (targetCategory === "alle") return true;
        return item.category_slug === targetCategory;
      });

      const seen = new Set();
      const result = [];

      for (const g of filteredGalleries) {
        if (Array.isArray(g.images)) {
          for (const imgUrl of g.images) {
            // Kun godkendte asset URLs
            if (typeof imgUrl === "string" && !seen.has(imgUrl)) {
              seen.add(imgUrl);
              result.push({
                url: imgUrl,
                title: g.title || "Hundens Pels og Pleje",
                alt: g.title ? `${g.title} - Hundens Pels og Pleje` : "Hund i salonen",
              });
            }
          }
        }
      }

      if (result.length > 0) {
        return maxImages ? result.slice(0, maxImages) : result;
      }
    }

    // Fallback til de 4 autentiske asset-billeder
    const fallbackList = ASSET_GALLERY_IMAGES.filter((item) => {
      if (targetCategory === "alle") return true;
      return item.categories.includes(targetCategory);
    });

    return maxImages ? fallbackList.slice(0, maxImages) : fallbackList;
  }, [gallerier, categorySlug, activeTab, maxImages]);

  const tabs = [
    { id: "alle", label: "Alle billeder" },
    { id: "klip", label: "Klip & formning" },
    { id: "vask-foen", label: "Vask & føn" },
    { id: "hvalpe", label: "Hvalpe" },
    { id: "foer-efter", label: "Før & efter" },
  ];

  function openLightbox(index) {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }

  const isBeforeAfterView = (categorySlug || activeTab) === "foer-efter";

  // Før & efter par fra CMS (med BEFORE_AFTER_PAIRS som pålidelig fallback)
  const activeBeforeAfterPairs = useMemo(() => {
    if (cmsBeforeAfterPairs && cmsBeforeAfterPairs.length > 0) {
      return cmsBeforeAfterPairs.map((item, idx) => ({
        id: item.id || `pair-${idx}`,
        title: item.title,
        description: item.description || "",
        before: {
          url: Array.isArray(item.before_image) ? item.before_image[0] : item.before_image,
          badge: "Før",
          alt: item.before_alt || `${item.title} før behandling`,
        },
        after: {
          url: Array.isArray(item.after_image) ? item.after_image[0] : item.after_image,
          badge: "Efter",
          alt: item.after_alt || `${item.title} efter behandling`,
        },
      }));
    }
    return BEFORE_AFTER_PAIRS;
  }, [cmsBeforeAfterPairs]);

  // Billedliste til lightbox afhængig af om vi er i Før & Efter eller standard galleri
  const lightboxImageList = useMemo(() => {
    if (isBeforeAfterView) {
      return activeBeforeAfterPairs.flatMap((pair) => [pair.before.url, pair.after.url]);
    }
    return displayedImages.map((i) => i.url);
  }, [isBeforeAfterView, activeBeforeAfterPairs, displayedImages]);

  function openBeforeAfterLightbox(index) {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }

  return (
    <section
      id={id}
      className="relative pt-14 pb-24 sm:pt-18 sm:pb-28 lg:pt-20 lg:pb-32 bg-white border-t border-[#E8DFD5] scroll-mt-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-black">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-3 text-base text-neutral-700 leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>

        {/* Category Filter Tabs */}
        {showTabs && !categorySlug && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-full px-5 py-2 text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-black text-white shadow-xs"
                    : "bg-transparent text-black border border-black/30 hover:border-black hover:text-black"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* FØR & EFTER DEDIKERET OPSÆTNING */}
        {isBeforeAfterView ? (
          <div className="mt-8 max-w-5xl mx-auto">
            {/* Parvise transformationer - simpel og kompakt opsætning */}
            <div className="space-y-6 sm:space-y-8">
              {activeBeforeAfterPairs.map((pair, pIdx) => (
                <div
                  key={pair.id}
                  className="rounded-2xl bg-[#FAF7F2] border border-[#E8DFD5] p-3.5 sm:p-5 shadow-2xs"
                >
                  {/* Kompakt header */}
                  <div className="mb-3 flex flex-wrap items-baseline justify-between gap-1 sm:gap-2">
                    <h3 className="font-heading text-base sm:text-lg font-medium text-black">
                      {pair.title}
                    </h3>
                    <p className="text-xs text-neutral-600">
                      {pair.description}
                    </p>
                  </div>

                  {/* Før & Efter side om side - mere kompakt */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 items-center">
                    {/* FØR BILLEDE */}
                    <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-[#EAE0D4] ring-1 ring-black/10 cursor-pointer">
                      <button
                        type="button"
                        onClick={() => openBeforeAfterLightbox(pIdx * 2)}
                        className="h-full w-full block text-left"
                        aria-label={`Åbn før-billede: ${pair.title}`}
                      >
                        <img
                          src={getOptimizedImageUrl(pair.before.url, { width: 600, quality: 85, fit: "cover" })}
                          alt={pair.before.alt}
                          loading="lazy"
                          className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute top-2 left-2 z-10">
                          <span className="inline-flex items-center gap-1 rounded-md bg-neutral-900/85 text-white px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider backdrop-blur-xs">
                            Før
                          </span>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-black shadow-xs">
                            <i className="fa-solid fa-magnifying-glass-plus text-sm" aria-hidden="true" />
                          </span>
                        </div>
                      </button>
                    </div>

                    {/* EFTER BILLEDE */}
                    <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-[#EAE0D4] ring-1 ring-black/10 cursor-pointer">
                      <button
                        type="button"
                        onClick={() => openBeforeAfterLightbox(pIdx * 2 + 1)}
                        className="h-full w-full block text-left"
                        aria-label={`Åbn efter-billede: ${pair.title}`}
                      >
                        <img
                          src={getOptimizedImageUrl(pair.after.url, { width: 600, quality: 85, fit: "cover" })}
                          alt={pair.after.alt}
                          loading="lazy"
                          className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute top-2 left-2 z-10">
                          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-700 text-white px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider shadow-xs">
                            Efter
                          </span>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-black shadow-xs">
                            <i className="fa-solid fa-magnifying-glass-plus text-sm" aria-hidden="true" />
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Standard Gallery Grid for andre kategorier */
          loading && gallerier.length === 0 ? (
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className="aspect-[4/3] animate-pulse rounded-2xl bg-[#EAE0D4]"
                />
              ))}
            </div>
          ) : displayedImages.length === 0 ? (
            <div className="mt-10 rounded-2xl border border-dashed border-[#DCCEC2] p-8 text-center text-sm text-black">
              Der er endnu ikke tilføjet billeder til denne kategori.
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {displayedImages.map((imgObj, idx) => (
                <button
                  key={`${imgObj.url}-${idx}`}
                  type="button"
                  onClick={() => openLightbox(idx)}
                  className="group relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[#F0E6DC] text-left ring-1 ring-black/10 focus:outline-hidden focus:ring-2 focus:ring-black transition-all duration-200 hover:shadow-md cursor-pointer"
                  aria-label={`Åbn billede ${idx + 1} i fuld størrelse`}
                >
                  <img
                    src={getOptimizedImageUrl(imgObj.url, { width: 700, quality: 85, fit: "cover" })}
                    alt={imgObj.alt || "Hund i salonen hos Hundens Pels og Pleje"}
                    loading="lazy"
                    className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                  />
                  {/* Hover overlay with zoom icon - strictly no text */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-black shadow-sm transition-transform duration-200 group-hover:scale-110">
                      <i className="fa-solid fa-magnifying-glass-plus text-base" aria-hidden="true" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )
        )}

        {/* Lightbox Modal */}
        <LightboxModal
          isOpen={lightboxOpen}
          currentIndex={lightboxIndex}
          images={lightboxImageList}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={setLightboxIndex}
        />

        {/* Link to full gallery when limited images are shown */}
        {maxImages && (
          <div className="mt-12 text-center">
            <Link
              to="/galleri"
              className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-7 py-3 text-sm font-medium tracking-wide text-black transition-colors hover:bg-black hover:text-white"
            >
              <i className="fa-solid fa-images text-black group-hover:text-white" aria-hidden="true" />
              <span>Se alle billeder i galleriet</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
